import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { API, formatWeight, sortBoxesNatural, todayIST, displayDate } from '@/lib/api';
import { useSubmitGuard } from '@/lib/useSubmitGuard';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DecimalInput } from '@/components/ui/decimal-input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Sun, Moon, CheckCircle, XCircle, Loader2, Lock, Upload, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

export default function StockTakingPage() {
  const { user } = useAuth();
  const canSeeCurrentGross = ['admin', 'store_manager', 'analyst'].includes(user?.role);
  const isAdmin = ['admin', 'analyst'].includes(user?.role);
  const [boxes, setBoxes] = useState([]);
  const [stockTime, setStockTime] = useState('morning');
  const [date, setDate] = useState(todayIST());
  const [entries, setEntries] = useState({});
  const [existingSnaps, setExistingSnaps] = useState([]);
  const [fourBoxGroups, setFourBoxGroups] = useState([]);
  const [groupWeight, setGroupWeight] = useState('');
  const [selectedBoxes, setSelectedBoxes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const { busy: actionBusy, run } = useSubmitGuard('Please wait — the previous submission is still processing');
  const [step, setStep] = useState(1);
  const [purityTab, setPurityTab] = useState('76');
  const [isLocked, setIsLocked] = useState(false);
  const [lockedSession, setLockedSession] = useState(null);
  const inputRefs = useRef({});
  const fileInputRef = useRef(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importErrors, setImportErrors] = useState([]);

  // Check session status + fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [boxRes, snapRes, statusRes] = await Promise.all([
          API.get('/boxes', { params: { active_only: true } }),
          API.get('/stock-taking/snapshots', { params: { date, stock_time: stockTime } }),
          API.get('/stock-taking/session-status', { params: { date, stock_time: stockTime } }),
        ]);
        setBoxes(boxRes.data);
        setExistingSnaps(snapRes.data);
        const existing = {};
        snapRes.data.forEach(s => {
          existing[s.box_id] = { gross_weight_g: s.gross_weight_g.toString(), gold_weight_g: (s.gold_weight_g || 0).toString(), polyethene_weight_g: (s.polyethene_weight_g || 0).toString() };
        });
        setEntries(existing);

        // Lock check: if session exists and user is not admin, lock the form
        if (statusRes.data.locked && !isAdmin) {
          setIsLocked(true);
          setLockedSession(statusRes.data.session);
        } else {
          setIsLocked(false);
          setLockedSession(statusRes.data.session);
        }
      } catch (e) { console.error(e); }
    };
    fetchData();
    setStep(1);
    setFourBoxGroups([]);
  }, [date, stockTime, isAdmin]);

  const handleEntryChange = (boxId, field, value) => {
    if (isLocked) return;
    setEntries(prev => ({ ...prev, [boxId]: { ...prev[boxId], [field]: value } }));
  };

  // Boxes sorted naturally and grouped by purity
  const sortedBoxes = useMemo(() => sortBoxesNatural(boxes), [boxes]);
  const boxesByPurity = useMemo(() => {
    const groups = { '76': [], '84': [], '92': [] };
    sortedBoxes.forEach(b => {
      const p = b.gold_purity || '';
      if (groups[p]) groups[p].push(b);
      else if (!groups['other']) groups['other'] = [b];
      else groups['other'].push(b);
    });
    return groups;
  }, [sortedBoxes]);

  const currentPurityBoxes = boxesByPurity[purityTab] || [];

  const handleKeyDown = useCallback((e, boxId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const idx = currentPurityBoxes.findIndex(b => b.box_id === boxId);
      if (idx < currentPurityBoxes.length - 1) {
        const nextBoxId = currentPurityBoxes[idx + 1].box_id;
        const nextInput = inputRefs.current[nextBoxId];
        if (nextInput) nextInput.focus();
      }
    }
  }, [currentPurityBoxes]);

  const registerRef = useCallback((boxId, el) => {
    if (el) inputRefs.current[boxId] = el;
  }, []);

  const handleSubmitAll = async () => {
    return run(async () => {
    setSubmitting(true);
    try {
      const snapshots = sortedBoxes.filter(b => entries[b.box_id]?.gross_weight_g).map(b => ({
        box_id: b.box_id, stock_time: stockTime, date,
        gross_weight_g: parseFloat(entries[b.box_id]?.gross_weight_g) || 0,
        gold_weight_g: parseFloat(entries[b.box_id]?.gold_weight_g) || 0,
        polyethene_weight_g: parseFloat(entries[b.box_id]?.polyethene_weight_g) || 0,
        submitted_by: user?.role || 'stock_manager',
      }));
      if (snapshots.length === 0) { toast.error('No entries to submit'); setSubmitting(false); return; }
      await API.post('/stock-taking/bulk-snapshot', snapshots);
      toast.success(`${snapshots.length} stock entries saved`);
      setStep(2);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error(err.response.data?.detail || 'Session is locked');
        setIsLocked(true);
      } else {
        toast.error('Failed to submit');
      }
    } finally { setSubmitting(false); }
    });
  };

  const handleDownloadSample = async () => {
    try {
      const res = await API.get('/stock-taking/import-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'stock_taking_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Sample template downloaded');
    } catch { toast.error('Failed to download template'); }
  };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (rows.length < 2) { toast.error('File is empty or has no data rows'); return; }
        // Find column indices from header
        const headerRow = rows[0].map(h => String(h || '').trim().toLowerCase());
        const nameCol = headerRow.findIndex(h => h.includes('box') && h.includes('name'));
        const weightCol = headerRow.findIndex(h => h.includes('gross') || h.includes('weight'));
        if (nameCol === -1 || weightCol === -1) {
          toast.error('File must have "Box Name" and "Box Gross Weight" columns');
          return;
        }
        const parsed = [];
        const errors = [];
        const boxIdSet = new Set(boxes.map(b => b.box_id.toUpperCase()));
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i];
          const boxName = String(row[nameCol] || '').trim();
          if (!boxName) continue;
          const rawWeight = row[weightCol];
          const weightStr = String(rawWeight ?? '').trim();
          if (weightStr === '') {
            // Empty weight — skip (user left it blank)
            continue;
          }
          const weight = Number(weightStr);
          if (isNaN(weight) || weightStr === '') {
            errors.push({ row: i + 1, boxName, value: weightStr, reason: `"${weightStr}" is not a valid number` });
            continue;
          }
          if (!boxIdSet.has(boxName.toUpperCase())) {
            errors.push({ row: i + 1, boxName, value: weightStr, reason: `Box "${boxName}" not found in active boxes` });
            continue;
          }
          // Find the actual box_id (case-insensitive match)
          const matchedBox = boxes.find(b => b.box_id.toUpperCase() === boxName.toUpperCase());
          parsed.push({ boxId: matchedBox.box_id, grossWeight: weight });
        }
        if (errors.length > 0) {
          setImportErrors(errors);
          setImportPreview(null);
          toast.error(`${errors.length} error(s) found in file. Fix them and re-upload.`);
        } else if (parsed.length === 0) {
          toast.error('No valid data rows found in file');
        } else {
          setImportErrors([]);
          setImportPreview(parsed);
        }
      } catch { toast.error('Failed to parse file. Make sure it is a valid .xlsx or .xls file.'); }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleConfirmImport = () => {
    if (!importPreview) return;
    setEntries(prev => {
      const updated = { ...prev };
      importPreview.forEach(({ boxId, grossWeight }) => {
        updated[boxId] = { ...updated[boxId], gross_weight_g: grossWeight.toString() };
      });
      return updated;
    });
    toast.success(`${importPreview.length} box weights imported`);
    setImportPreview(null);
  };

  const toggleBoxForGroup = (boxId) => {
    if (isLocked) return;
    setSelectedBoxes(prev => {
      if (prev.includes(boxId)) return prev.filter(b => b !== boxId);
      if (prev.length >= 4) return prev;
      return [...prev, boxId];
    });
  };

  const handleFourBoxCheck = async () => {
    if (selectedBoxes.length !== 4) { toast.error('Select exactly 4 boxes'); return; }
    if (!groupWeight) { toast.error('Enter group weight'); return; }
    return run(async () => {
    try {
      const res = await API.post('/stock-taking/four-box-check', {
        box_ids: selectedBoxes, group_weight_g: parseFloat(groupWeight),
        stock_time: stockTime, date, submitted_by: user?.role || 'stock_manager',
      });
      setFourBoxGroups(prev => [...prev, { ...res.data, box_ids: selectedBoxes }]);
      if (res.data.matches) toast.success('4-box group MATCHES!');
      else toast.error(`MISMATCH: difference of ${res.data.difference_g}g`);
      setSelectedBoxes([]); setGroupWeight('');
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error(err.response.data?.detail || 'Session is locked');
        setIsLocked(true);
      } else {
        toast.error(err.response?.data?.detail || 'Check failed');
      }
    }
    });
  };

  // Count entries per purity
  const purityCounts = useMemo(() => {
    const counts = {};
    ['76', '84', '92'].forEach(p => {
      const pBoxes = boxesByPurity[p] || [];
      const filled = pBoxes.filter(b => entries[b.box_id]?.gross_weight_g).length;
      counts[p] = { total: pBoxes.length, filled };
    });
    return counts;
  }, [boxesByPurity, entries]);

  const handleSubmitStockTaking = async () => {
    return run(async () => {
      setSubmitting(true);
      try {
        await API.post('/stock-taking/finalize', { date, stock_time: stockTime });
        toast.success('Stock taking submitted! Admin has been notified.');
        setIsLocked(true);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to submit');
      } finally { setSubmitting(false); }
    });
  };

  const steps = [{ num: 1, label: 'Weights' }, { num: 2, label: '4-Box Match & Submit' }];

  return (
    <div>
      <div className="page-header">
        <h1>Stock Taking</h1>
        <p className="hidden sm:block">Record daily stock weights box by box</p>
      </div>

      {/* Locked Banner */}
      {isLocked && (
        <div className="mb-4 p-3 rounded-lg border-2 border-amber-600/40 bg-amber-950/20 flex items-center gap-3" data-testid="stock-taking-locked-banner">
          <Lock size={18} className="text-amber-400 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-300">Stock Taking Submitted</p>
            <p className="text-xs text-muted-foreground">
              This session ({displayDate(date)} - {stockTime}) has already been submitted
              {lockedSession?.submitted_by && ` by ${lockedSession.submitted_by}`}.
              {isAdmin ? ' As admin, you can edit via ST Results page.' : ' Contact admin to make changes.'}
            </p>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 mb-4 md:mb-6">
        <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full sm:w-44" data-testid="stocktaking-date-picker" />
        <div className="flex rounded-lg border overflow-hidden" data-testid="stocktaking-shift-toggle">
          <button className={`flex-1 px-3 py-2 flex items-center justify-center gap-2 text-sm font-medium transition-colors ${stockTime === 'morning' ? 'bg-amber-100 text-amber-800' : 'bg-white text-muted-foreground'}`} onClick={() => setStockTime('morning')}>
            <Sun size={16} /> Morning
          </button>
          <button className={`flex-1 px-3 py-2 flex items-center justify-center gap-2 text-sm font-medium transition-colors ${stockTime === 'night' ? 'bg-indigo-100 text-indigo-800' : 'bg-white text-muted-foreground'}`} onClick={() => setStockTime('night')}>
            <Moon size={16} /> Night
          </button>
        </div>
        {!isLocked && (
          <div className="flex gap-2 sm:ml-auto">
            <Button variant="outline" size="sm" onClick={handleDownloadSample} data-testid="stocktaking-download-sample" className="text-xs">
              <Download size={14} className="mr-1" />Sample File
            </Button>
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="stocktaking-import-btn" className="text-xs text-amber-400 border-amber-500/30 hover:bg-amber-500/10">
              <Upload size={14} className="mr-1" />Import File
            </Button>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleImportFile} className="hidden" data-testid="stocktaking-import-input" />
          </div>
        )}
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-1 sm:gap-2 mb-4 md:mb-6 overflow-x-auto">
        {steps.map((s, i) => (
          <div key={s.num} className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <button onClick={() => !isLocked && setStep(s.num)} disabled={isLocked}
              className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold transition-colors ${step === s.num ? 'bg-primary text-primary-foreground' : step > s.num ? 'bg-emerald-100 text-emerald-800' : 'bg-muted text-muted-foreground'} ${isLocked ? 'opacity-60 cursor-not-allowed' : ''}`}>
              {step > s.num ? <CheckCircle size={14} /> : s.num}
            </button>
            <span className={`text-xs sm:text-sm ${step === s.num ? 'font-medium' : 'text-muted-foreground'}`}>{s.label}</span>
            {i < steps.length - 1 && <div className="w-6 sm:w-12 h-px bg-border" />}
          </div>
        ))}
      </div>

      {/* Step 1: Enter Weights by Purity */}
      {step === 1 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base md:text-lg">Enter Box Weights</CardTitle>
          </CardHeader>
          <CardContent className="p-0 sm:p-6 sm:pt-0">
            {/* Purity Tabs */}
            <div className="flex items-center gap-2 px-3 sm:px-0 mb-4" data-testid="stocktaking-purity-tabs">
              {['76', '84', '92'].map(p => (
                <button key={p} data-testid={`stocktaking-purity-tab-${p}`}
                  onClick={() => setPurityTab(p)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors purity-${p} ${purityTab === p ? 'ring-2 ring-primary ring-offset-1 ring-offset-background' : 'opacity-60 hover:opacity-90'}`}>
                  {p} ({purityCounts[p]?.filled || 0}/{purityCounts[p]?.total || 0})
                </button>
              ))}
            </div>

            {/* Mobile: card view */}
            <div className="block sm:hidden p-3 space-y-2">
              {currentPurityBoxes.map((box, idx) => (
                <div key={box.box_id} className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono font-semibold text-sm">{box.box_id}</span>
                    {entries[box.box_id]?.gross_weight_g ? (
                      existingSnaps.find(s => s.box_id === box.box_id)
                        ? <Badge variant="outline" className="text-emerald-600 text-[10px]">Saved</Badge>
                        : <Badge variant="outline" className="text-amber-600 text-[10px]">New</Badge>
                    ) : null}
                  </div>
                  {canSeeCurrentGross && <div className="text-xs text-muted-foreground mb-1">Current: {formatWeight(box.gross_weight_g)}</div>}
                  <input
                    ref={el => registerRef(box.box_id, el)}
                    type="text" inputMode="decimal"
                    value={entries[box.box_id]?.gross_weight_g || ''}
                    onChange={e => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) handleEntryChange(box.box_id, 'gross_weight_g', v); }}
                    onKeyDown={e => handleKeyDown(e, box.box_id)}
                    placeholder="Gross weight (g)"
                    disabled={isLocked}
                    className={`flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm font-mono placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${entries[box.box_id]?.gross_weight_g === '0' ? 'border-rose-500 text-rose-400 bg-rose-500/10' : 'border-input'} ${isLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                  />
                </div>
              ))}
              {currentPurityBoxes.length === 0 && <p className="text-center py-4 text-sm text-muted-foreground">No boxes with purity {purityTab}</p>}
            </div>

            {/* Desktop: table view */}
            <div className="hidden sm:block">
              <div className="table-scroll-wrapper">
                <Table className="data-table" data-testid="stocktaking-box-list">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">#</TableHead>
                      <TableHead>Box ID</TableHead>
                      {canSeeCurrentGross && <TableHead>Current Gross</TableHead>}
                      <TableHead>Recorded Gross (g)</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentPurityBoxes.map((box, idx) => (
                      <TableRow key={box.box_id}>
                        <TableCell className="text-muted-foreground text-xs">{idx + 1}</TableCell>
                        <TableCell className="font-mono font-semibold">{box.box_id}</TableCell>
                        {canSeeCurrentGross && <TableCell className="weight-value">{formatWeight(box.gross_weight_g)}</TableCell>}
                        <TableCell>
                          <input
                            ref={el => registerRef(box.box_id, el)}
                            type="text" inputMode="decimal"
                            value={entries[box.box_id]?.gross_weight_g || ''}
                            onChange={e => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) handleEntryChange(box.box_id, 'gross_weight_g', v); }}
                            onKeyDown={e => handleKeyDown(e, box.box_id)}
                            placeholder="0.000"
                            disabled={isLocked}
                            className={`flex h-9 w-36 rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm font-mono placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${entries[box.box_id]?.gross_weight_g === '0' ? 'border-rose-500 text-rose-400 bg-rose-500/10' : 'border-input'} ${isLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                          />
                        </TableCell>
                        <TableCell>
                          {entries[box.box_id]?.gross_weight_g ? (
                            existingSnaps.find(s => s.box_id === box.box_id) ? <Badge variant="outline" className="text-emerald-600">Saved</Badge> : <Badge variant="outline" className="text-amber-600">New</Badge>
                          ) : <span className="text-sm text-muted-foreground">-</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                    {currentPurityBoxes.length === 0 && (
                      <TableRow><TableCell colSpan={canSeeCurrentGross ? 5 : 4} className="text-center py-8 text-muted-foreground">No boxes with purity {purityTab}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="p-3 sm:pt-4 flex justify-end">
              <Button onClick={handleSubmitAll} disabled={submitting || isLocked} className="w-full sm:w-auto" data-testid="stocktaking-submit-button">
                {isLocked ? <><Lock className="mr-2" size={16} />Submitted</> : submitting ? <><Loader2 className="animate-spin mr-2" size={16} />Saving...</> : 'Save All & Continue'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: 4-Box Matching + Submit */}
      {step === 2 && (
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base md:text-lg">4-Box Group Matching</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3">Select 4 boxes, enter combined weight to verify.</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 mb-4">
                {sortedBoxes.map(box => (
                  <button key={box.box_id} onClick={() => toggleBoxForGroup(box.box_id)}
                    disabled={isLocked}
                    className={`p-2 sm:p-3 rounded-lg border text-xs sm:text-sm font-mono transition-colors ${selectedBoxes.includes(box.box_id) ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:bg-muted'} ${isLocked ? 'opacity-60 cursor-not-allowed' : ''}`}>
                    {box.box_id}
                  </button>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
                <span className="text-xs sm:text-sm font-medium truncate">Selected: {selectedBoxes.join(', ') || 'None'}</span>
                <div className="flex gap-2">
                  <DecimalInput className="flex-1 sm:w-44" value={groupWeight} onChange={v => setGroupWeight(v)} placeholder="Group weight (g)" disabled={isLocked} />
                  <Button onClick={handleFourBoxCheck} disabled={selectedBoxes.length !== 4 || isLocked || actionBusy} className="whitespace-nowrap">{actionBusy ? 'Checking…' : 'Check'}</Button>
                </div>
              </div>
            </CardContent>
          </Card>
          {fourBoxGroups.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Results</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3" data-testid="stocktaking-4box-group-card">
                  {fourBoxGroups.map((g, i) => (
                    <div key={i} className={`p-3 rounded-lg border-2 ${g.matches ? 'border-emerald-300 bg-emerald-50' : 'border-rose-300 bg-rose-50'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {g.matches ? <CheckCircle className="text-emerald-600" size={18} /> : <XCircle className="text-rose-600" size={18} />}
                          <span className="font-semibold text-sm">{g.matches ? 'MATCH' : 'MISMATCH'}</span>
                        </div>
                        <span className="font-mono text-xs">{g.box_ids?.join(' + ')}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-3 text-xs sm:text-sm">
                        <span>Group: <b className="font-mono">{formatWeight(g.group_weight_g)}</b></span>
                        <span>Sum: <b className="font-mono">{formatWeight(g.individual_total_g)}</b></span>
                        {!g.matches && <span className="text-rose-600">Diff: <b className="font-mono">{formatWeight(g.difference_g)}</b></span>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Summary + Submit */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base md:text-lg">Stock Taking Summary</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <p><b>Date:</b> {displayDate(date)}</p>
                <p><b>Shift:</b> {stockTime === 'morning' ? 'Morning' : 'Night'}</p>
                <p><b>Boxes recorded:</b> {Object.keys(entries).filter(k => entries[k]?.gross_weight_g).length} / {boxes.length}</p>
                {['76', '84', '92'].map(p => (
                  <p key={p}><b>Purity {p}:</b> {purityCounts[p]?.filled || 0} / {purityCounts[p]?.total || 0} recorded</p>
                ))}
                <p><b>4-Box checks:</b> {fourBoxGroups.length} ({fourBoxGroups.filter(g => g.matches).length} matched)</p>
              </div>
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setStep(1)} disabled={isLocked} className="w-full sm:w-auto">Back to Weights</Button>
                {!isLocked ? (
                  <Button onClick={handleSubmitStockTaking} disabled={submitting} className="w-full sm:w-auto" data-testid="stocktaking-submit-final">
                    {submitting ? <><Loader2 className="animate-spin mr-2" size={16} />Submitting...</> : 'Submit Stock Taking'}
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-amber-400">
                    <Lock size={14} />
                    <span>Already submitted</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Import Preview Dialog */}
      <Dialog open={!!importPreview} onOpenChange={(open) => { if (!open) setImportPreview(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload size={18} /> Import Preview</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-3">
            {importPreview?.length} box weight(s) will be imported. Confirm to fill them into the gross weight fields.
          </p>
          <div className="border rounded-lg overflow-hidden max-h-[50vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Box Name</TableHead>
                  <TableHead className="text-xs text-right">Gross Weight (g)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {importPreview?.map(({ boxId, grossWeight }) => (
                  <TableRow key={boxId} className={grossWeight === 0 ? 'bg-rose-500/10' : ''}>
                    <TableCell className="font-mono text-sm font-semibold">{boxId}</TableCell>
                    <TableCell className={`text-right font-mono text-sm ${grossWeight === 0 ? 'text-rose-400 font-bold' : ''}`}>
                      {grossWeight.toFixed(3)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {importPreview?.some(p => p.grossWeight === 0) && (
            <p className="text-xs text-rose-400 mt-2">Rows highlighted in red have zero gross weight.</p>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setImportPreview(null)} data-testid="import-cancel-btn">Cancel</Button>
            <Button onClick={handleConfirmImport} data-testid="import-confirm-btn">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Errors Dialog */}
      <Dialog open={importErrors.length > 0} onOpenChange={(open) => { if (!open) setImportErrors([]); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-400"><XCircle size={18} /> Import Errors</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-3">
            The file contains errors. Fix them and re-upload. No data was imported.
          </p>
          <div className="border rounded-lg overflow-hidden border-rose-500/30 max-h-[50vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Row</TableHead>
                  <TableHead className="text-xs">Box Name</TableHead>
                  <TableHead className="text-xs">Value</TableHead>
                  <TableHead className="text-xs">Error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {importErrors.map((err, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs">{err.row}</TableCell>
                    <TableCell className="font-mono text-xs">{err.boxName}</TableCell>
                    <TableCell className="font-mono text-xs text-rose-400">{err.value}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{err.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportErrors([])} data-testid="import-errors-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
